import React, { useEffect, useState } from 'react';
import { Paper, Stack, Title, Text, Group, Skeleton, TextInput, ActionIcon, Select } from '@mantine/core';
import { CalendarDateInput } from '../../../global/component/molecules/CalendarDateInput';
import { IconEdit, IconCheck, IconX } from '@tabler/icons-react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { useGetPatientProfileSummary } from '../../../global/entities/Patients/queries/useGetPatientProfileSummary.tsx';
import { useUpdateFileNumberMutation } from '../../../global/entities/Patients/mutations/useUpdateFileNumberMutation.tsx';
import { useUpdateBirthDateMutation } from '../../../global/entities/Patients/mutations/useUpdateBirthDateMutation.tsx';
import { useUpdateSexMutation } from '../../../global/entities/Patients/mutations/useUpdateSexMutation.tsx';
import { useGetSexes } from '../../../global/entities/Sexes/queries/useGetSexes.ts';
import Utils from '../../../global/helpers/utils.ts';
import PatientProgramInfoWidget from './PatientProgramInfoWidget.tsx';

// The backend stores the date of birth as a UTC-anchored calendar day. Reopen it
// as a local Date so the calendar prefills on the stored day and the save path
// (local getters) round-trips it unchanged in every timezone.
const toLocalCalendarDay = (value: string): Date | null => {
    const formatted = Utils.formatDateToYYYYMMDD(new Date(value));
    if (!formatted) return null;
    const [year, month, day] = formatted.split('-').map(Number);
    return new Date(year, month - 1, day);
};

interface IPatientDataBoxProps {
    patientStore?: PatientStore;
}

const PatientDataBox: React.FC<IPatientDataBoxProps> = inject('patientStore')(
    observer(({ patientStore }) => {
        const { t } = useTranslation();
        const { data: profile, isLoading } = useGetPatientProfileSummary(patientStore?.patientId);
        const updateFileNumber = useUpdateFileNumberMutation(patientStore?.patientId);
        const updateBirthDate = useUpdateBirthDateMutation(patientStore?.patientId);
        const updateSex = useUpdateSexMutation(patientStore?.patientId);
        const { data: sexes } = useGetSexes();
        const [isEditingFileNumber, setIsEditingFileNumber] = useState(false);
        const [editValue, setEditValue] = useState('');
        const [isEditingBirthDate, setIsEditingBirthDate] = useState(false);
        const [birthDateValue, setBirthDateValue] = useState<Date | null>(null);
        const [birthDateError, setBirthDateError] = useState<string | null>(null);
        const [isEditingSex, setIsEditingSex] = useState(false);
        const [sexValue, setSexValue] = useState<string | null>(null);

        useEffect(() => {
            if (!profile?.birthDate || patientStore?.birthDate === profile.birthDate) return;
            patientStore?.setPatientBirthDate(profile.birthDate);
        }, [profile?.birthDate, patientStore]);

        const handleEditFileNumber = () => {
            setEditValue(profile?.fileNumber || '');
            setIsEditingFileNumber(true);
        };

        const handleSaveFileNumber = () => {
            updateFileNumber.mutate(editValue || null, {
                onSuccess: () => setIsEditingFileNumber(false),
            });
        };

        const handleCancelEdit = () => {
            setIsEditingFileNumber(false);
        };

        const handleEditBirthDate = () => {
            setBirthDateValue(profile?.birthDate ? toLocalCalendarDay(profile.birthDate) : null);
            setBirthDateError(null);
            setIsEditingBirthDate(true);
        };

        const isBirthDateOutOfRange = (date: Date): boolean => {
            const today = new Date();
            const age =
                today.getFullYear() -
                date.getFullYear() -
                (today < new Date(today.getFullYear(), date.getMonth(), date.getDate()) ? 1 : 0);
            return age < 0 || age > 120;
        };

        const handleSaveBirthDate = () => {
            if (!birthDateValue) return;
            if (isBirthDateOutOfRange(birthDateValue)) {
                setBirthDateError(t('formError.birthDateOutOfRange'));
                return;
            }
            setBirthDateError(null);
            updateBirthDate.mutate(birthDateValue, {
                onSuccess: () => setIsEditingBirthDate(false),
            });
        };

        const handleCancelBirthDate = () => {
            setBirthDateError(null);
            setIsEditingBirthDate(false);
        };

        const sexOptions = (sexes ?? []).map((s) => ({ value: s.id.toString(), label: s.name }));

        const handleEditSex = () => {
            setSexValue(profile?.sexID != null ? profile.sexID.toString() : null);
            setIsEditingSex(true);
        };

        const handleSaveSex = () => {
            if (!sexValue) return;
            updateSex.mutate(parseInt(sexValue, 10), {
                onSuccess: () => setIsEditingSex(false),
            });
        };

        const handleCancelSex = () => {
            setIsEditingSex(false);
        };

        const rows = [
            { label: t('formProfile.therapeuticArea'), value: profile?.therapeuticIndication || '-' },
            { label: t('formProfile.primaryPhysician'), value: profile?.primaryHcpName || '-' },
            { label: 'Email', value: profile?.email || '-' },
        ];

        return (
            <Paper h={'100%'} style={{ display: 'flex', flexDirection: 'column' }}>
                <Stack>
                    <Title order={4}>{profile?.name || patientStore?.name || '-'}</Title>
                    {isLoading ? (
                        <Stack gap="xs">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Skeleton key={i} height={20} />
                            ))}
                        </Stack>
                    ) : (
                        <Stack gap="xs">
                            <Group gap="xs">
                                <Text size="sm" fw={600} c="dimmed">
                                    {t('formGlobal.fileNumber')}:
                                </Text>
                                {isEditingFileNumber ? (
                                    <Group gap={4} wrap="nowrap">
                                        <TextInput
                                            size="xs"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.currentTarget.value)}
                                            maxLength={50}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleSaveFileNumber();
                                                if (e.key === 'Escape') handleCancelEdit();
                                            }}
                                            autoFocus
                                        />
                                        <ActionIcon size="sm" variant="subtle" color="green" onClick={handleSaveFileNumber} loading={updateFileNumber.isPending}>
                                            <IconCheck size={14} />
                                        </ActionIcon>
                                        <ActionIcon size="sm" variant="subtle" color="gray" onClick={handleCancelEdit}>
                                            <IconX size={14} />
                                        </ActionIcon>
                                    </Group>
                                ) : (
                                    <Group gap={4} wrap="nowrap">
                                        <Text size="sm">{profile?.fileNumber || '-'}</Text>
                                        <ActionIcon size="sm" variant="subtle" color="gray" onClick={handleEditFileNumber}>
                                            <IconEdit size={14} />
                                        </ActionIcon>
                                    </Group>
                                )}
                            </Group>
                            <Group gap="xs">
                                <Text size="sm" fw={600} c="dimmed">
                                    {t('formProfile.birthDate')}:
                                </Text>
                                {isEditingBirthDate ? (
                                    <Group gap={4} wrap="nowrap">
                                        <CalendarDateInput
                                            size="xs"
                                            placeholder={t('formProfile.birthDatePlaceholder')}
                                            value={birthDateValue}
                                            error={birthDateError}
                                            onChange={(value) => {
                                                setBirthDateValue(value ? new Date(value) : null);
                                                setBirthDateError(null);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleSaveBirthDate();
                                                if (e.key === 'Escape') handleCancelBirthDate();
                                            }}
                                            autoFocus
                                        />
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="green"
                                            aria-label={t('formProfile.save')}
                                            onClick={handleSaveBirthDate}
                                            disabled={!birthDateValue}
                                            loading={updateBirthDate.isPending}
                                        >
                                            <IconCheck size={14} />
                                        </ActionIcon>
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="gray"
                                            aria-label={t('formProfile.cancel')}
                                            onClick={handleCancelBirthDate}
                                        >
                                            <IconX size={14} />
                                        </ActionIcon>
                                    </Group>
                                ) : (
                                    <Group gap={4} wrap="nowrap">
                                        <Text size="sm">
                                            {profile?.birthDate ? Utils.formatDateToYYYYMMDD(new Date(profile.birthDate)) : '-'}
                                        </Text>
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="gray"
                                            aria-label={t('formProfile.editBirthDate')}
                                            onClick={handleEditBirthDate}
                                        >
                                            <IconEdit size={14} />
                                        </ActionIcon>
                                    </Group>
                                )}
                            </Group>
                            <Group gap="xs">
                                <Text size="sm" fw={600} c="dimmed">
                                    {t('formProfile.sexIDTitle')}:
                                </Text>
                                {isEditingSex ? (
                                    <Group gap={4} wrap="nowrap">
                                        <Select
                                            size="xs"
                                            aria-label={t('formProfile.sexIDTitle')}
                                            placeholder={t('formProfile.sexIDPlaceholder')}
                                            data={sexOptions}
                                            value={sexValue}
                                            onChange={setSexValue}
                                            allowDeselect={false}
                                            comboboxProps={{ withinPortal: false }}
                                            autoFocus
                                        />
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="green"
                                            aria-label={t('formProfile.save')}
                                            onClick={handleSaveSex}
                                            disabled={!sexValue}
                                            loading={updateSex.isPending}
                                        >
                                            <IconCheck size={14} />
                                        </ActionIcon>
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="gray"
                                            aria-label={t('formProfile.cancel')}
                                            onClick={handleCancelSex}
                                        >
                                            <IconX size={14} />
                                        </ActionIcon>
                                    </Group>
                                ) : (
                                    <Group gap={4} wrap="nowrap">
                                        <Text size="sm">{profile?.sex || '-'}</Text>
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="gray"
                                            aria-label={t('formProfile.editSex')}
                                            onClick={handleEditSex}
                                        >
                                            <IconEdit size={14} />
                                        </ActionIcon>
                                    </Group>
                                )}
                            </Group>
                            {rows.map((row) => (
                                <Group key={row.label} gap="xs">
                                    <Text size="sm" fw={600} c="dimmed">
                                        {row.label}:
                                    </Text>
                                    <Text size="sm">{row.value}</Text>
                                </Group>
                            ))}
                        </Stack>
                    )}
                    <PatientProgramInfoWidget />
                </Stack>
            </Paper>
        );
    }),
);

export default PatientDataBox;
